import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import Logo from "@/components/Logo";
import FooterV2 from "@/components/homev2/FooterV2";
import { Button } from "@/components/ui/button";
import testimonialRaphael from "@/assets/testimonial-raphael-new.png";
import reel1 from "@/assets/reel-1.png";
import videoCard1 from "@/assets/video-card-1.webp";
import videoCard3 from "@/assets/video-card-3.webp";

const serif = { fontWeight: 600, letterSpacing: "-0.01em" } as const;

const realisations = [
  { image: reel1, label: "Reels", href: "/reels", alt: "Miniature d'un reel produit par Agence 80/20" },
  { image: videoCard1, label: "Podcasts", href: "/podcasts", alt: "Miniature d'un podcast produit par Agence 80/20" },
  { image: videoCard3, label: "Événements", href: "/evenements", alt: "Miniature d'une captation événementielle" },
];

const Merci = () => (
  <>
    <SEOHead
      title="Merci — Agence 8020"
      description="Nous avons reçu votre demande. Un membre de l'équipe Agence 80/20 vous contactera sous peu."
      canonical="/merci"
      noIndex
    />

    <div className="min-h-screen bg-background text-foreground" lang="fr-CA">
      <header className="border-b border-border">
        <div className="mx-auto flex h-16 max-w-[1280px] items-center px-5 md:px-8">
          <Logo variant="full" size="sm" />
        </div>
      </header>

      <main id="main-content">
        <section className="mx-auto max-w-[900px] px-5 py-20 md:px-8 md:py-28">
          <h1 className="text-3xl leading-tight text-foreground md:text-5xl" style={serif}>
            Merci. Nous allons regarder votre demande.
          </h1>
          <p className="mt-6 text-base leading-relaxed text-muted-foreground md:text-lg">
            Un membre de l'équipe 80/20 va prendre connaissance de vos réponses et vous contacter pour mieux comprendre
            votre situation, ce que vous avez déjà essayé et où votre système semble bloquer.
          </p>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
            Pendant ce temps, découvrez quelques-uns des systèmes et contenus que nous avons construits pour nos
            partenaires.
          </p>
        </section>

        <section aria-labelledby="etude-titre" className="border-y border-border/60 bg-secondary/20 py-16 md:py-20">
          <div className="mx-auto max-w-[1280px] px-5 md:px-8">
            <h2 id="etude-titre" className="text-2xl text-foreground md:text-3xl" style={serif}>
              Une étude de cas
            </h2>
            <div className="mt-6 rounded-2xl border border-border bg-card p-6 md:p-8">
              <span
                className="inline-block rounded-full border border-dashed border-primary/40 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-primary"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                Contenu à confirmer
              </span>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                Emplacement réservé pour une étude de cas vérifiable : contexte, leviers activés et résultats mesurés.
              </p>
              <Link to="/etudes-de-cas" className="mt-6 inline-flex items-center gap-1.5 text-sm text-foreground hover:text-primary">
                Voir les études de cas
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        <section aria-labelledby="realisations-titre" className="py-16 md:py-20">
          <div className="mx-auto max-w-[1280px] px-5 md:px-8">
            <h2 id="realisations-titre" className="text-2xl text-foreground md:text-3xl" style={serif}>
              Quelques réalisations
            </h2>
            <ul className="mt-6 grid gap-5 sm:grid-cols-3">
              {realisations.map((item) => (
                <li key={item.label}>
                  <Link to={item.href} className="group block overflow-hidden rounded-2xl border border-border bg-card">
                    <div className="aspect-[4/5] overflow-hidden">
                      <img src={item.image} alt={item.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
                    </div>
                    <span className="block p-4 text-base text-foreground" style={serif}>{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>

            <figure className="mt-12 grid gap-6 rounded-2xl border border-border bg-card p-6 md:grid-cols-[200px_1fr] md:p-8">
              <img src={testimonialRaphael} alt="Raphael Gagné, Wisera Technologies" loading="lazy" decoding="async" className="h-48 w-full rounded-xl object-cover object-[center_20%] md:h-full" />
              <div>
                <blockquote className="text-sm leading-relaxed text-muted-foreground md:text-base">
                  « Ce que j'aime avec l'Agence 8020, c'est qu'ils ne font pas juste exécuter : ils proposent des idées
                  créatives qui sortent du cadre. Grâce à eux, Wisera a gagné en visibilité, et on reçoit maintenant des
                  leads directement de notre contenu sur les réseaux sociaux. »
                </blockquote>
                <figcaption className="mt-4">
                  <span className="block text-base text-foreground">Raphael Gagné</span>
                  <span className="block text-sm text-primary">Wisera Technologies</span>
                </figcaption>
              </div>
            </figure>

            <div className="mt-12 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button asChild size="lg" className="rounded-full bg-primary px-8 py-6 text-base text-primary-foreground hover:bg-primary/90">
                <Link to="/portfolio">Voir le portfolio complet</Link>
              </Button>
              <Link to="/plan-de-match" className="text-sm text-muted-foreground underline-offset-4 hover:text-primary hover:underline sm:ml-4">
                En savoir plus sur le Plan de match
              </Link>
            </div>
          </div>
        </section>
      </main>

      <FooterV2 />
    </div>
  </>
);

export default Merci;
